import { create } from 'zustand';
import { mockStudents } from '../lib/mockData/students';
import { mockBands, mockClasses, mockFormGroups, mockSets, mockYearGroups } from '../pages/Students/MockData';
import type { Band, Class, FormGroup, Set, Student, YearGroup } from '../types';

interface StudentState {
  // Data
  students: Student[];
  yearGroups: YearGroup[];
  classes: Class[];
  formGroups: FormGroup[];
  sets: Set[];
  bands: Band[];

  // Year Group operations
  addYearGroup: (yearGroup: YearGroup) => void;
  updateYearGroup: (id: string, data: Partial<YearGroup>) => void;
  deleteYearGroup: (id: string) => void;

  // Class operations
  addClass: (classData: Class) => void;
  updateClass: (id: string, data: Partial<Class>) => void;
  deleteClass: (id: string) => void;

  // Form Group operations
  addFormGroup: (formGroup: FormGroup) => void;
  updateFormGroup: (id: string, data: Partial<FormGroup>) => void;
  deleteFormGroup: (id: string) => void;

  // Set operations
  addSet: (set: Set) => void;
  updateSet: (id: string, data: Partial<Set>) => void;
  deleteSet: (id: string) => void;

  // Band operations
  addBand: (band: Band) => void;
  updateBand: (id: string, data: Partial<Band>) => void;
  deleteBand: (id: string) => void;

  // Student operations
  addStudent: (student: Student) => void;
  updateStudent: (id: string, data: Partial<Student>) => void;
  deleteStudent: (id: string) => void;
  assignStudentToClass: (studentId: string, classId: string) => void;
  assignStudentToFormGroup: (studentId: string, formGroupId: string) => void;
  assignStudentToSet: (studentId: string, setId: string) => void;
  removeStudentFromSet: (studentId: string, setId: string) => void;
  assignStudentToBand: (studentId: string, bandId: string) => void;
  removeStudentFromBand: (studentId: string, bandId: string) => void;

  // Bulk operations
  assignStudentsToClass: (studentIds: string[], classId: string) => void;
  assignStudentsToSet: (studentIds: string[], setId: string) => void;
}

export const useStudentStore = create<StudentState>((set, _get) => ({
  // Initial state
  students: mockStudents,
  yearGroups: mockYearGroups,
  classes: mockClasses,
  formGroups: mockFormGroups,
  sets: mockSets,
  bands: mockBands,

  // Year Group operations
  addYearGroup: (yearGroup) =>
    set((state) => ({
      yearGroups: [...state.yearGroups, yearGroup],
    })),

  updateYearGroup: (id, data) =>
    set((state) => ({
      yearGroups: state.yearGroups.map((yg) => (yg.id === id ? { ...yg, ...data } : yg)),
    })),

  deleteYearGroup: (id) =>
    set((state) => ({
      yearGroups: state.yearGroups.filter((yg) => yg.id !== id),
      // Cascade delete classes, form groups, sets, bands
      classes: state.classes.filter((c) => c.yearGroupId !== id),
      formGroups: state.formGroups.filter((fg) => fg.yearGroupId !== id),
      sets: state.sets.filter((s) => s.yearGroupId !== id),
      bands: state.bands.filter((b) => b.yearGroupId !== id),
      // Remove year group from students
      students: state.students.filter((s) => s.yearGroupId !== id),
    })),

  // Class operations
  addClass: (classData) =>
    set((state) => ({
      classes: [...state.classes, classData],
    })),

  updateClass: (id, data) =>
    set((state) => ({
      classes: state.classes.map((c) => (c.id === id ? { ...c, ...data } : c)),
    })),

  deleteClass: (id) =>
    set((state) => ({
      classes: state.classes.filter((c) => c.id !== id),
      // Remove class from students
      students: state.students.map((s) => (s.classId === id ? { ...s, classId: undefined } : s)),
    })),

  // Form Group operations
  addFormGroup: (formGroup) =>
    set((state) => ({
      formGroups: [...state.formGroups, formGroup],
    })),

  updateFormGroup: (id, data) =>
    set((state) => ({
      formGroups: state.formGroups.map((fg) => (fg.id === id ? { ...fg, ...data } : fg)),
    })),

  deleteFormGroup: (id) =>
    set((state) => ({
      formGroups: state.formGroups.filter((fg) => fg.id !== id),
      // Remove form group from students
      students: state.students.map((s) => (s.formGroupId === id ? { ...s, formGroupId: undefined } : s)),
    })),

  // Set operations
  addSet: (set) =>
    set((state) => ({
      sets: [...state.sets, set],
    })),

  updateSet: (id, data) =>
    set((state) => ({
      sets: state.sets.map((s) => (s.id === id ? { ...s, ...data } : s)),
    })),

  deleteSet: (id) =>
    set((state) => ({
      sets: state.sets.filter((s) => s.id !== id),
      // Remove set from students
      students: state.students.map((s) => ({
        ...s,
        sets: s.sets.filter((setId) => setId !== id),
      })),
    })),

  // Band operations
  addBand: (band) =>
    set((state) => ({
      bands: [...state.bands, band],
    })),

  updateBand: (id, data) =>
    set((state) => ({
      bands: state.bands.map((b) => (b.id === id ? { ...b, ...data } : b)),
    })),

  deleteBand: (id) =>
    set((state) => ({
      bands: state.bands.filter((b) => b.id !== id),
      // Remove band from students
      students: state.students.map((s) => ({
        ...s,
        bands: s.bands.filter((bandId) => bandId !== id),
      })),
    })),

  // Student operations
  addStudent: (student) =>
    set((state) => ({
      students: [...state.students, student],
    })),

  updateStudent: (id, data) =>
    set((state) => ({
      students: state.students.map((s) => (s.id === id ? { ...s, ...data } : s)),
    })),

  deleteStudent: (id) =>
    set((state) => ({
      students: state.students.filter((s) => s.id !== id),
    })),

  assignStudentToClass: (studentId, classId) =>
    set((state) => ({
      students: state.students.map((s) => (s.id === studentId ? { ...s, classId } : s)),
    })),

  assignStudentToFormGroup: (studentId, formGroupId) =>
    set((state) => ({
      students: state.students.map((s) => (s.id === studentId ? { ...s, formGroupId } : s)),
    })),

  assignStudentToSet: (studentId, setId) =>
    set((state) => ({
      students: state.students.map((s) => (s.id === studentId ? { ...s, sets: [...s.sets, setId] } : s)),
    })),

  removeStudentFromSet: (studentId, setId) =>
    set((state) => ({
      students: state.students.map((s) =>
        s.id === studentId
          ? {
              ...s,
              sets: s.sets.filter((id) => id !== setId),
            }
          : s,
      ),
    })),

  assignStudentToBand: (studentId, bandId) =>
    set((state) => ({
      students: state.students.map((s) => (s.id === studentId ? { ...s, bands: [...s.bands, bandId] } : s)),
    })),

  removeStudentFromBand: (studentId, bandId) =>
    set((state) => ({
      students: state.students.map((s) =>
        s.id === studentId
          ? {
              ...s,
              bands: s.bands.filter((id) => id !== bandId),
            }
          : s,
      ),
    })),

  // Bulk operations
  assignStudentsToClass: (studentIds, classId) =>
    set((state) => ({
      students: state.students.map((s) => (studentIds.includes(s.id) ? { ...s, classId } : s)),
    })),

  assignStudentsToSet: (studentIds, setId) =>
    set((state) => ({
      students: state.students.map((s) =>
        studentIds.includes(s.id) && !s.sets.includes(setId) ? { ...s, sets: [...s.sets, setId] } : s,
      ),
    })),
}));
